import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { toast } from 'react-toastify';

import AuthContext from '../../context/authContext';
import { usePizzaBranch } from '../../context/pizzaBranchContext';
import useInternalBooking from '../../hooks/useInternalBooking';
import { generateUniqueColor } from '../../util/colorUtil';
import { ATTENTION_WINDOW_MS, attentionStripeBackground } from '../../util/attentionStripes';
import BookingDetailsModal from '../Bookings/BookingDetailsModal/BookingDetailsModal';
import KitchenOrdersBoard from './KitchenOrdersBoard';

dayjs.locale('de');

const DELIVERED_VISIBLE_MS = 60 * 60 * 1000;
const PIZZA_VARIETIES = [
  { value: 'margherita', label: 'Margherita' },
  { value: 'salami', label: 'Salami' },
  { value: 'halfHalf', label: 'Halb/Halb (Margherita/Salami)' },
];

const formatDateTimeLocal = value => {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : '';
};

const formatBookingOption = booking => {
  const time = booking?.startTime ? dayjs(booking.startTime).format('HH:mm') : '–';
  return `${time} · ${booking.customerName} · #${booking.bookingNumber}`;
};

const PizzaStatusButton = ({ delivered, loading, disabled, onClick }) => (
  <Tooltip
    title={
      disabled
        ? 'Keine Berechtigung zum Abhaken'
        : delivered
          ? 'Pizza-Ausgabe zurücksetzen'
          : 'Pizza als ausgegeben markieren'
    }
  >
    <span>
      <IconButton
        size='large'
        onClick={onClick}
        disabled={disabled || loading}
        sx={{
          width: 48,
          height: 48,
          border: '2px solid',
          borderColor: delivered ? 'success.main' : 'divider',
          backgroundColor: delivered ? 'success.light' : 'background.paper',
          color: delivered ? 'success.dark' : 'text.disabled',
          '&:hover': {
            backgroundColor: delivered ? 'success.light' : 'action.hover',
          },
        }}
      >
        {loading ? (
          <CircularProgress size={22} />
        ) : delivered ? (
          <CheckIcon fontSize='medium' />
        ) : null}
      </IconButton>
    </span>
  </Tooltip>
);

const PizzaTimelineSkeleton = () => (
  <Stack spacing={2}>
    {[0, 1, 2].map(key => (
      <Skeleton key={key} variant='rounded' height={128} />
    ))}
  </Stack>
);

const ServiceTimeField = ({ booking, disabled, loading, onSave }) => {
  const [value, setValue] = useState(formatDateTimeLocal(booking.plannedServeAt));

  useEffect(() => {
    setValue(formatDateTimeLocal(booking.plannedServeAt));
  }, [booking.plannedServeAt]);

  const handleBlur = () => {
    if (!value) return;
    const next = dayjs(value);
    if (!next.isValid()) {
      toast.error('Bitte eine gültige Servierzeit eintragen.');
      setValue(formatDateTimeLocal(booking.plannedServeAt));
      return;
    }
    const current = formatDateTimeLocal(booking.plannedServeAt);
    if (value !== current) onSave(booking, next.toISOString());
  };

  return (
    <TextField
      size='small'
      type='datetime-local'
      label='Geplante Servierzeit'
      value={value}
      onChange={event => setValue(event.target.value)}
      onBlur={handleBlur}
      disabled={disabled || loading}
      InputLabelProps={{ shrink: true }}
      sx={{ minWidth: { xs: '100%', sm: 220 } }}
    />
  );
};

const PizzaCommentField = ({ booking, disabled, loading, onSave }) => {
  // Uncontrolled (ref-based) to avoid a re-render on every keystroke.
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = booking.comment || '';
  }, [booking.comment]);

  const handleBlur = () => {
    const next = (inputRef.current?.value || '').trim();
    if (next === (booking.comment || '')) return;
    onSave(booking, next);
  };

  return (
    <TextField
      size='small'
      label='Kommentar'
      placeholder='Notiz zur Pizza-Buchung'
      defaultValue={booking.comment || ''}
      inputRef={inputRef}
      onBlur={handleBlur}
      disabled={disabled || loading}
      inputProps={{ maxLength: 280 }}
      sx={{ width: '100%' }}
    />
  );
};

const PizzaItems = ({
  booking,
  disabled,
  loading,
  onAllIncDeliveredChange,
  onAllIncVarietyChange,
  onItemDeliveredChange,
}) => (
  <Stack spacing={1}>
    {(booking.pizzaItems || []).map(item => {
      if (item.isAllInc) {
        return (
          <Accordion key={item.itemId || item.upgradeId} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                <Chip size='small' color='warning' label={item.name} />
                <Typography variant='body2'>
                  {item.count} Pizzen nach Personenanzahl
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {(item.pizzas || []).map(pizza => (
                  <Stack
                    key={pizza.slotIndex}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <PizzaStatusButton
                      delivered={!!pizza.delivered}
                      loading={loading}
                      disabled={disabled}
                      onClick={() =>
                        onAllIncDeliveredChange(booking, pizza.slotIndex, !pizza.delivered)
                      }
                    />
                    <Typography variant='body2' sx={{ minWidth: 72, fontWeight: 600 }}>
                      {pizza.label}
                    </Typography>
                    <FormControl size='small' sx={{ minWidth: { xs: 0, sm: 180 }, width: { xs: '100%', sm: 'auto' } }} disabled={disabled || loading}>
                      <InputLabel id={`${booking._id}-${pizza.slotIndex}-pizza-variety`}>
                        Sorte
                      </InputLabel>
                      <Select
                        labelId={`${booking._id}-${pizza.slotIndex}-pizza-variety`}
                        label='Sorte'
                        value={pizza.variety || ''}
                        onChange={event =>
                          onAllIncVarietyChange(booking, pizza.slotIndex, event.target.value)
                        }
                      >
                        <MenuItem value=''>
                          <em>Noch offen</em>
                        </MenuItem>
                        {PIZZA_VARIETIES.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      }

      if (Array.isArray(item.pizzas) && item.pizzas.length > 0) {
        return (
          <Accordion key={item.itemId || item.upgradeId} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' useFlexGap>
                <Chip
                  size='small'
                  color={item.isManual ? 'secondary' : 'warning'}
                  label={item.isManual ? `Vor Ort: ${item.name}` : item.name}
                />
                <Typography variant='body2'>
                  {item.pizzas.length} Pizzen
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {item.pizzas.map(pizza => (
                  <Stack
                    key={`${pizza.itemKey}-${pizza.slotIndex}`}
                    direction='row'
                    spacing={1}
                    alignItems='center'
                  >
                    <PizzaStatusButton
                      delivered={!!pizza.delivered}
                      loading={loading}
                      disabled={disabled}
                      onClick={() => onItemDeliveredChange(booking, pizza, !pizza.delivered)}
                    />
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {pizza.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      }

      return (
        <Chip
          key={item.itemId || `${item.upgradeId}-${item.name}`}
          size='small'
          color={item.isManual ? 'secondary' : 'warning'}
          variant='outlined'
          label={`${item.isManual ? 'Vor Ort: ' : ''}${item.name}${item.count > 1 ? ` (${item.count}x)` : ''}`}
          sx={{ alignSelf: 'flex-start' }}
        />
      );
    })}
  </Stack>
);

const PizzaTimelineEntry = ({
  booking,
  isLast,
  rowLoading,
  serviceLoading,
  canUpdateDelivery,
  nowTs,
  onToggleDelivery,
  onOpenDetails,
  onTableNumberChange,
  onServiceTimeSave,
  onCommentSave,
  onItemDeliveredChange,
  onAllIncDeliveredChange,
  onAllIncVarietyChange,
}) => {
  const theme = useTheme();
  const deliveredAt = booking.pizzaFulfillment?.deliveredAt
    ? new Date(booking.pizzaFulfillment.deliveredAt).getTime()
    : null;
  const delivered = !!deliveredAt;
  const plannedServeAt = new Date(booking.plannedServeAt || booking.startTime).getTime();
  const remainingMs = plannedServeAt - nowTs;
  const startsInMinutes = Math.round(remainingMs / 60000);
  const serviceLabel =
    startsInMinutes >= 0 ? `in ${startsInMinutes} Min.` : `seit ${Math.abs(startsInMinutes)} Min.`;
  // Attention states: pizzas due within the next 15 minutes get warning stripes,
  // pizzas whose serve time has passed are emphasised as "due now".
  const isDue = !delivered && remainingMs <= 0;
  const inAttentionWindow = !delivered && remainingMs > 0 && remainingMs <= ATTENTION_WINDOW_MS;
  const paperBg = delivered
    ? alpha(theme.palette.success.main, theme.palette.mode === 'light' ? 0.06 : 0.16)
    : theme.palette.background.paper;
  let background = paperBg;
  let borderColor = delivered ? 'success.light' : 'divider';
  if (isDue) {
    background = attentionStripeBackground(theme, { color: 'error' });
    borderColor = 'error.main';
  } else if (inAttentionWindow) {
    background = attentionStripeBackground(theme, { color: 'warning' });
    borderColor = 'warning.main';
  }
  const timeColor = isDue ? 'error.main' : inAttentionWindow ? 'warning.main' : 'text.secondary';
  const textDecoration = delivered ? 'line-through' : 'none';
  const slotColor = booking.slotColor?.color || null;
  const maxTableNumber = Number(booking.maxTableNumber || 0);
  const unusableTableSet = new Set(
    (Array.isArray(booking.unusableTables) ? booking.unusableTables : []).map(Number)
  );
  const currentTableNumber = Number(booking.tableNumber) || 0;
  // Hide tables that are out of service, but keep a table that is already
  // assigned to this booking so the existing selection is never lost.
  const tableOptions = Array.from({ length: maxTableNumber }, (_, index) => index + 1).filter(
    number => !unusableTableSet.has(number) || number === currentTableNumber
  );

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '48px minmax(0, 1fr)', sm: '96px minmax(0, 1fr)' }, gap: { xs: 1, sm: 2 }, width: '100%', minWidth: 0 }}>
      <Stack alignItems='center' sx={{ pt: { xs: 0.5, sm: 0 } }}>
        <Typography sx={{ fontWeight: 800, lineHeight: 1, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
          {dayjs(booking.plannedServeAt || booking.startTime).format('HH:mm')}
        </Typography>
        <Typography
          variant='caption'
          color={timeColor}
          align='center'
          sx={{ fontWeight: isDue || inAttentionWindow ? 700 : 400, lineHeight: 1.2, mt: 0.25 }}
        >
          {serviceLabel}
        </Typography>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.25, sm: 2 },
          minWidth: 0,
          overflow: 'hidden',
          border: isDue || inAttentionWindow ? '2px solid' : '1px solid',
          borderLeft: slotColor ? `6px solid ${slotColor}` : undefined,
          borderColor,
          background,
          opacity: delivered ? 0.78 : 1,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
          <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between'>
            <PizzaStatusButton
              delivered={delivered}
              loading={rowLoading}
              disabled={!canUpdateDelivery}
              onClick={() => onToggleDelivery(booking)}
            />
            <Tooltip title='Buchung öffnen'>
              <IconButton
                size='small'
                onClick={() => onOpenDetails(booking)}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              >
                <OpenInNewIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              flexWrap='wrap'
              useFlexGap
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent='space-between'
            >
              <Stack spacing={1} sx={{ textDecoration, minWidth: { xs: 0, md: 200 }, flex: { md: 1 } }}>
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap alignItems='center' sx={{ minWidth: 0 }}>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 700, fontSize: { xs: '1.05rem', sm: '1.25rem' }, overflowWrap: 'break-word' }}
                  >
                    {booking.customerName}
                  </Typography>
                  <Chip size='small' label={`${booking.participantCount} Personen`} />
                  <Chip size='small' variant='outlined' label={`#${booking.bookingNumber}`} />
                  {isDue && <Chip size='small' color='error' label='Jetzt fällig' />}
                  {inAttentionWindow && <Chip size='small' color='warning' label='Gleich fällig' />}
                  {booking.slotColor?.label && (
                    <Chip
                      size='small'
                      label={`Slot ${booking.slotColor.label}`}
                      sx={{ backgroundColor: booking.slotColor.color, color: '#fff' }}
                    />
                  )}
                  {delivered && (
                    <Chip
                      size='small'
                      color='success'
                      label={`ausgegeben ${dayjs(booking.pizzaFulfillment.deliveredAt).format('HH:mm')}`}
                    />
                  )}
                </Stack>
                <Typography variant='caption' color='text.secondary'>
                  Buchung {dayjs(booking.startTime).format('HH:mm')}–{dayjs(booking.endTime).format('HH:mm')}
                </Typography>
              </Stack>

              <ServiceTimeField
                booking={booking}
                disabled={!canUpdateDelivery}
                loading={serviceLoading}
                onSave={onServiceTimeSave}
              />
              {maxTableNumber > 0 && (
                <FormControl size='small' sx={{ minWidth: { xs: '100%', sm: 160 } }} disabled={!canUpdateDelivery || serviceLoading}>
                  <InputLabel id={`${booking._id}-pizza-table-label`}>Tisch</InputLabel>
                  <Select
                    labelId={`${booking._id}-pizza-table-label`}
                    label='Tisch'
                    value={booking.tableNumber || ''}
                    onChange={event => onTableNumberChange(booking, event.target.value)}
                  >
                    <MenuItem value=''>
                      <em>Kein Tisch</em>
                    </MenuItem>
                    {tableOptions.map(number => (
                      <MenuItem key={number} value={number}>
                        Tisch {number}
                        {unusableTableSet.has(number) ? ' (defekt)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>

            <PizzaCommentField
              booking={booking}
              disabled={!canUpdateDelivery}
              loading={serviceLoading}
              onSave={onCommentSave}
            />

            <PizzaItems
              booking={booking}
              disabled={!canUpdateDelivery}
              loading={serviceLoading}
              onItemDeliveredChange={onItemDeliveredChange}
              onAllIncDeliveredChange={onAllIncDeliveredChange}
              onAllIncVarietyChange={onAllIncVarietyChange}
            />
          </Stack>

          <Tooltip title='Buchung öffnen'>
            <IconButton
              size='small'
              onClick={() => onOpenDetails(booking)}
              sx={{ alignSelf: 'center', display: { xs: 'none', sm: 'inline-flex' } }}
            >
              <OpenInNewIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>
    </Box>
  );
};

const AddPizzaDialog = ({
  open,
  options,
  loading,
  selectedBookingId,
  selectedUpgradeId,
  count,
  fullScreen,
  onBookingChange,
  onUpgradeChange,
  onCountChange,
  onClose,
  onSubmit,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth fullScreen={fullScreen}>
    <DialogTitle>Vor Ort gekaufte Pizza hinzufügen</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ mt: 1 }}>
        <FormControl fullWidth size='small' disabled={loading || (options.bookings || []).length === 0}>
          <InputLabel id='pizza-add-booking-label'>Buchung</InputLabel>
          <Select
            labelId='pizza-add-booking-label'
            label='Buchung'
            value={selectedBookingId}
            onChange={event => onBookingChange(event.target.value)}
          >
            {(options.bookings || []).map(booking => (
              <MenuItem key={booking._id} value={booking._id}>
                {formatBookingOption(booking)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size='small' disabled={loading || (options.pizzaUpsells || []).length === 0}>
          <InputLabel id='pizza-add-upgrade-label'>Pizza</InputLabel>
          <Select
            labelId='pizza-add-upgrade-label'
            label='Pizza'
            value={selectedUpgradeId}
            onChange={event => onUpgradeChange(event.target.value)}
          >
            {(options.pizzaUpsells || []).map(upgrade => (
              <MenuItem key={upgrade.upgradeId} value={upgrade.upgradeId}>
                {upgrade.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          size='small'
          type='number'
          label='Anzahl'
          value={count}
          onChange={event => onCountChange(event.target.value)}
          inputProps={{ min: 1, max: 20 }}
          disabled={loading}
        />

        {(options.bookings || []).length === 0 && (
          <Alert severity='info'>Aktuell sind keine anwesenden Buchungen auswählbar.</Alert>
        )}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>
        Abbrechen
      </Button>
      <Button
        onClick={onSubmit}
        variant='contained'
        disabled={loading || !selectedBookingId || !selectedUpgradeId}
        startIcon={loading ? <CircularProgress size={16} /> : null}
      >
        Hinzufügen
      </Button>
    </DialogActions>
  </Dialog>
);

// Merged kitchen overview ("Küche"). Formerly two separate screens — the pizza
// booking overview and the live kitchen view — combined into one. It keeps the
// full pizza booking timeline and additionally embeds the live online food-order
// board (KitchenOrdersBoard), so ordered dishes appear here too and the standalone
// kitchen view is gone.
const PizzaView = () => {
  const authCtx = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    addPizzaManualItem,
    getPizzaOptions,
    getPizzaOverview,
    markCustomerAsPresent,
    updateInternalBooking,
    updatePizzaDelivery,
    updatePizzaService,
  } = useInternalBooking();
  const {
    branchOptions,
    selectedBranchId,
    setSelectedBranchId,
    showBranchSelect,
    branchesReady,
  } = usePizzaBranch();
  const [bookings, setBookings] = useState([]);
  // Walk-in pizzas ordered online via the customer "Speisekarte" (independent of
  // bookings). Rendered as a visually distinct section with extra info.
  const [onlineOrders, setOnlineOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [rowLoading, setRowLoading] = useState({});
  const [serviceLoading, setServiceLoading] = useState({});
  const [nowTs, setNowTs] = useState(() => Date.now());
  const [pendingPresenceBooking, setPendingPresenceBooking] = useState(null);
  const [presenceLoading, setPresenceLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [pizzaOptions, setPizzaOptions] = useState({ bookings: [], pizzaUpsells: [] });
  const [pizzaOptionsLoading, setPizzaOptionsLoading] = useState(false);
  const [selectedAddBookingId, setSelectedAddBookingId] = useState('');
  const [selectedAddUpgradeId, setSelectedAddUpgradeId] = useState('');
  const [selectedAddCount, setSelectedAddCount] = useState(1);
  const [detailsModal, setDetailsModal] = useState({
    open: false,
    bookingId: null,
    borderColor: null,
  });

  const canRead = authCtx.checkRightAny('internalBooking:read:own');
  const canUpdateDelivery = authCtx.checkRightAny('internalBooking:update:minor');
  // The merged kitchen view also shows the live online food orders ("Speisen"),
  // but only for users who may read food orders. Users with pizza rights only keep
  // the read-only online-pizza section as before.
  const canReadFood = authCtx.checkRightAny('foodOrder:read:own');

  const load = async ({ showLoading = false } = {}) => {
    if (showLoading) setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getPizzaOverview({
        branchId: selectedBranchId || undefined,
      });
      setBookings(Array.isArray(response?.bookings) ? response.bookings : []);
      setOnlineOrders(Array.isArray(response?.onlineOrders) ? response.onlineOrders : []);
    } catch (err) {
      setLoadError(err?.message || 'Küchen-Übersicht konnte nicht geladen werden.');
      toast.error('Fehler beim Laden der Küchen-Übersicht');
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  const loadPizzaOptions = async () => {
    setPizzaOptionsLoading(true);
    try {
      const response = await getPizzaOptions({ branchId: selectedBranchId || undefined });
      const nextOptions = {
        bookings: Array.isArray(response?.bookings) ? response.bookings : [],
        pizzaUpsells: Array.isArray(response?.pizzaUpsells) ? response.pizzaUpsells : [],
      };
      setPizzaOptions(nextOptions);
      setSelectedAddBookingId(prev =>
        prev && nextOptions.bookings.some(booking => booking._id === prev)
          ? prev
          : nextOptions.bookings[0]?._id || ''
      );
      setSelectedAddUpgradeId(prev =>
        prev && nextOptions.pizzaUpsells.some(upgrade => upgrade.upgradeId === prev)
          ? prev
          : nextOptions.pizzaUpsells[0]?.upgradeId || ''
      );
    } catch (err) {
      toast.error('Fehler beim Laden der Pizza-Auswahl');
    } finally {
      setPizzaOptionsLoading(false);
    }
  };

  useEffect(() => {
    if (!canRead || !branchesReady) return undefined;
    load({ showLoading: true });
    if (addDialogOpen) loadPizzaOptions();
    const interval = setInterval(() => {
      setNowTs(Date.now());
      load({ showLoading: false });
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [canRead, branchesReady, selectedBranchId]);

  const visibleBookings = useMemo(() => {
    const filtered = bookings.filter(booking => {
      const deliveredAt = booking.pizzaFulfillment?.deliveredAt
        ? new Date(booking.pizzaFulfillment.deliveredAt).getTime()
        : null;
      return !deliveredAt || nowTs - deliveredAt < DELIVERED_VISIBLE_MS;
    });
    // Keep the server's time ordering, but push fully delivered bookings to the
    // bottom so the next pizzas due stay at the top of the list.
    return filtered
      .map((booking, index) => ({ booking, index }))
      .sort((a, b) => {
        const aDone = !!a.booking.pizzaFulfillment?.deliveredAt;
        const bDone = !!b.booking.pizzaFulfillment?.deliveredAt;
        if (aDone !== bDone) return aDone ? 1 : -1;
        return a.index - b.index;
      })
      .map(entry => entry.booking);
  }, [bookings, nowTs]);

  // Accessible for pizza-booking staff (internalBooking) and/or food-order staff.
  if (!canRead && !canReadFood) {
    return <Navigate to='/welcome' replace />;
  }

  const updateBookingRow = (bookingId, updater) => {
    setBookings(prev => prev.map(row => (row._id === bookingId ? updater(row) : row)));
  };

  const applyPizzaFulfillmentToItems = (pizzaItems = [], fulfillment = {}) => {
    const itemDeliveriesByKey = new Map(
      (fulfillment.itemDeliveries || []).map(entry => [
        `${entry.itemKey}:${Number(entry.slotIndex)}`,
        entry,
      ])
    );
    const bySlot = new Map((fulfillment.allIncPizzas || []).map(entry => [Number(entry.slotIndex), entry]));
    return pizzaItems.map(item => {
      if (!item.isAllInc) {
        if (!Array.isArray(item.pizzas)) return item;
        return {
          ...item,
          pizzas: item.pizzas.map(pizza => {
            const next = itemDeliveriesByKey.get(`${pizza.itemKey}:${Number(pizza.slotIndex)}`);
            return next
              ? {
                  ...pizza,
                  deliveredAt: next.deliveredAt || null,
                  deliveredBy: next.deliveredBy || null,
                  delivered: !!next.deliveredAt,
                }
              : {
                  ...pizza,
                  deliveredAt: null,
                  deliveredBy: null,
                  delivered: false,
                };
          }),
        };
      }
      return {
        ...item,
        pizzas: (item.pizzas || []).map(pizza => {
          const next = bySlot.get(Number(pizza.slotIndex));
          return next
            ? {
                ...pizza,
                variety: next.variety || null,
                deliveredAt: next.deliveredAt || null,
                deliveredBy: next.deliveredBy || null,
                delivered: !!next.deliveredAt,
              }
            : {
                ...pizza,
                deliveredAt: null,
                deliveredBy: null,
                delivered: false,
              };
        }),
      };
    });
  };

  const updateLocalPizzaFulfillment = (bookingId, nextFulfillment, extra = {}) => {
    updateBookingRow(bookingId, row => ({
      ...row,
      ...extra,
      pizzaItems: applyPizzaFulfillmentToItems(row.pizzaItems || [], nextFulfillment),
      pizzaFulfillment: {
        delivered: !!nextFulfillment.deliveredAt,
        deliveredAt: nextFulfillment.deliveredAt || null,
        deliveredBy: nextFulfillment.deliveredBy || null,
      },
    }));
  };

  const handleItemDeliveredChange = async (booking, pizza, delivered) => {
    const itemDeliveries = (booking.pizzaItems || [])
      .filter(item => !item.isAllInc && Array.isArray(item.pizzas))
      .flatMap(item =>
        item.pizzas.map(unit => ({
          itemKey: unit.itemKey,
          slotIndex: unit.slotIndex,
          delivered:
            unit.itemKey === pizza.itemKey && unit.slotIndex === pizza.slotIndex
              ? delivered
              : !!unit.delivered,
        }))
      );

    setServiceLoading(prev => ({ ...prev, [booking._id]: true }));
    try {
      const response = await updatePizzaService({ bookingId: booking._id, itemDeliveries });
      const nextFulfillment = response?.booking?.pizzaFulfillment || {};
      updateLocalPizzaFulfillment(booking._id, nextFulfillment);
    } catch (err) {
      toast.error('Fehler beim Speichern des Pizza-Status');
    } finally {
      setServiceLoading(prev => ({ ...prev, [booking._id]: false }));
    }
  };

  const markPizzaDelivery = async (booking, delivered) => {
    if (!booking?._id) return false;
    setRowLoading(prev => ({ ...prev, [booking._id]: true }));
    try {
      const response = await updatePizzaDelivery({
        bookingId: booking._id,
        delivered,
      });
      const nextFulfillment = response?.booking?.pizzaFulfillment || {};
      updateLocalPizzaFulfillment(booking._id, nextFulfillment);
      return true;
    } catch (err) {
      toast.error('Fehler beim Aktualisieren der Pizza-Ausgabe');
      return false;
    } finally {
      setRowLoading(prev => ({ ...prev, [booking._id]: false }));
    }
  };

  const handleToggleDelivery = async booking => {
    if (!booking?._id) return;
    const delivered = !booking.pizzaFulfillment?.delivered;
    if (delivered && !booking.presentSince) {
      setPendingPresenceBooking(booking);
      return;
    }
    await markPizzaDelivery(booking, delivered);
  };

  const handleConfirmPresenceAndDelivery = async () => {
    if (!pendingPresenceBooking?._id) return;
    const booking = pendingPresenceBooking;
    setPresenceLoading(true);
    setRowLoading(prev => ({ ...prev, [booking._id]: true }));
    try {
      const presentResponse = await markCustomerAsPresent({
        bookingId: booking._id,
        body: {},
      });
      const presentSince = presentResponse?.updatedBooking?.presentSince || new Date().toISOString();
      updateBookingRow(booking._id, row => ({ ...row, presentSince }));
      const delivered = await markPizzaDelivery({ ...booking, presentSince }, true);
      if (delivered) setPendingPresenceBooking(null);
    } catch (err) {
      toast.error('Fehler beim Markieren als anwesend');
    } finally {
      setPresenceLoading(false);
      setRowLoading(prev => ({ ...prev, [booking._id]: false }));
    }
  };

  const handleOpenDetails = booking => {
    if (!booking?._id) return;
    setDetailsModal({
      open: true,
      bookingId: booking._id,
      borderColor: booking.slotColor?.color || generateUniqueColor(String(booking._id)),
    });
  };

  const handleCloseDetails = () => {
    setDetailsModal({ open: false, bookingId: null, borderColor: null });
  };

  const handleServiceTimeSave = async (booking, plannedServeAt) => {
    setServiceLoading(prev => ({ ...prev, [booking._id]: true }));
    try {
      await updatePizzaService({ bookingId: booking._id, plannedServeAt });
      updateBookingRow(booking._id, row => ({
        ...row,
        plannedServeAt,
        plannedServeAtOverride: plannedServeAt,
      }));
    } catch (err) {
      toast.error('Fehler beim Speichern der Servierzeit');
    } finally {
      setServiceLoading(prev => ({ ...prev, [booking._id]: false }));
    }
  };

  const handleCommentSave = async (booking, comment) => {
    setServiceLoading(prev => ({ ...prev, [booking._id]: true }));
    try {
      const response = await updatePizzaService({ bookingId: booking._id, comment });
      const nextComment = response?.booking?.pizzaFulfillment?.comment || '';
      updateBookingRow(booking._id, row => ({ ...row, comment: nextComment }));
    } catch (err) {
      toast.error('Fehler beim Speichern des Kommentars');
    } finally {
      setServiceLoading(prev => ({ ...prev, [booking._id]: false }));
    }
  };

  const handleTableNumberChange = async (booking, tableNumber) => {
    const nextTableNumber = tableNumber ? Number(tableNumber) : null;
    setServiceLoading(prev => ({ ...prev, [booking._id]: true }));
    try {
      await updateInternalBooking(booking._id, { tableNumber: nextTableNumber });
      updateBookingRow(booking._id, row => ({ ...row, tableNumber: nextTableNumber }));
    } catch (err) {
      toast.error('Fehler beim Speichern der Tischnummer');
    } finally {
      setServiceLoading(prev => ({ ...prev, [booking._id]: false }));
    }
  };

  const handleAllIncDeliveredChange = async (booking, slotIndex, delivered) => {
    const allIncItem = (booking.pizzaItems || []).find(item => item.isAllInc);
    if (!allIncItem) return;
    const allIncPizzas = (allIncItem.pizzas || []).map(pizza => ({
      slotIndex: pizza.slotIndex,
      variety: pizza.variety || null,
      delivered: pizza.slotIndex === slotIndex ? delivered : !!pizza.delivered,
    }));

    setServiceLoading(prev => ({ ...prev, [booking._id]: true }));
    try {
      const response = await updatePizzaService({ bookingId: booking._id, allIncPizzas });
      const nextFulfillment = response?.booking?.pizzaFulfillment || {};
      updateLocalPizzaFulfillment(booking._id, nextFulfillment);
    } catch (err) {
      toast.error('Fehler beim Speichern des Pizza-Status');
    } finally {
      setServiceLoading(prev => ({ ...prev, [booking._id]: false }));
    }
  };

  const handleAllIncVarietyChange = async (booking, slotIndex, variety) => {
    const allIncItem = (booking.pizzaItems || []).find(item => item.isAllInc);
    if (!allIncItem) return;
    const allIncPizzas = (allIncItem.pizzas || []).map(pizza => ({
      slotIndex: pizza.slotIndex,
      variety: pizza.slotIndex === slotIndex ? variety || null : pizza.variety || null,
    }));

    setServiceLoading(prev => ({ ...prev, [booking._id]: true }));
    try {
      await updatePizzaService({ bookingId: booking._id, allIncPizzas });
      updateBookingRow(booking._id, row => ({
        ...row,
        pizzaItems: (row.pizzaItems || []).map(item =>
          item.isAllInc
            ? {
                ...item,
                pizzas: (item.pizzas || []).map(pizza =>
                  pizza.slotIndex === slotIndex ? { ...pizza, variety: variety || null } : pizza
                ),
              }
            : item
        ),
      }));
    } catch (err) {
      toast.error('Fehler beim Speichern der Pizza-Sorte');
    } finally {
      setServiceLoading(prev => ({ ...prev, [booking._id]: false }));
    }
  };

  const handleOpenAddDialog = async () => {
    setAddDialogOpen(true);
    await loadPizzaOptions();
  };

  const handleCloseAddDialog = () => {
    if (pizzaOptionsLoading) return;
    setAddDialogOpen(false);
  };

  const handleAddManualItem = async () => {
    const count = Math.max(1, Number(selectedAddCount || 1));
    setPizzaOptionsLoading(true);
    try {
      await addPizzaManualItem({
        bookingId: selectedAddBookingId,
        upgradeId: selectedAddUpgradeId,
        count,
      });
      toast.success('Pizza hinzugefügt');
      setAddDialogOpen(false);
      setSelectedAddCount(1);
      await load({ showLoading: false });
      await loadPizzaOptions();
    } catch (err) {
      toast.error('Fehler beim Hinzufügen der Pizza');
    } finally {
      setPizzaOptionsLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1120, mx: 'auto', px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, sm: 3 } }}>
      <Stack spacing={{ xs: 2, sm: 3 }}>
        <Box>
          <Typography variant='h4' sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
            Küche
          </Typography>
        </Box>

        {canRead && (
          <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              {showBranchSelect && (
                <FormControl size='small' sx={{ minWidth: { xs: '100%', sm: 260 } }} disabled={!branchesReady}>
                  <InputLabel id='pizza-branch-label'>Standort</InputLabel>
                  <Select
                    labelId='pizza-branch-label'
                    label='Standort'
                    value={selectedBranchId}
                    onChange={event => setSelectedBranchId(event.target.value)}
                  >
                    {branchOptions.map(option => (
                      <MenuItem key={option.id || 'all'} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
                disabled={!canUpdateDelivery || !branchesReady}
                sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
              >
                Pizza hinzufügen
              </Button>
            </Stack>
          </Paper>
        )}

        {canRead && !canUpdateDelivery && (
          <Alert severity='info'>
            Du kannst die Pizza-Liste ansehen, aber ohne Bearbeitungsrecht keine Ausgabe abhaken.
          </Alert>
        )}

        {canRead && loadError && <Alert severity='error'>{loadError}</Alert>}

        {canReadFood && (
          <KitchenOrdersBoard branchId={selectedBranchId} selfManageBranch={!canRead} />
        )}

        {!canReadFood && onlineOrders.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              border: '2px dashed',
              borderColor: theme.palette.secondary.main,
              background: alpha(theme.palette.secondary.main, 0.06),
            }}
          >
            <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap' sx={{ mb: 1.5 }}>
              <Chip label='ONLINE' color='secondary' size='small' />
              <Typography variant='h6'>Online-Bestellungen (Speisekarte)</Typography>
              <Typography variant='body2' color='text.secondary'>
                Unabhängig von Buchungen – direkt online bestellt.
              </Typography>
            </Stack>
            <Stack spacing={1.5}>
              {onlineOrders.map(order => (
                <Box
                  key={order._id}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1,
                    borderLeft: '4px solid',
                    borderLeftColor: theme.palette.secondary.main,
                    background: alpha(theme.palette.background.paper, 0.9),
                  }}
                >
                  <Box>
                    <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                      <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                        #{order.orderNumber}
                      </Typography>
                      <Chip
                        size='small'
                        label={order.tableNumber ? `Tisch ${order.tableNumber}` : 'Abholung'}
                      />
                      <Chip
                        size='small'
                        color={order.status === 'ready' ? 'success' : 'warning'}
                        label={order.status === 'ready' ? 'Abholbereit' : 'In Zubereitung'}
                      />
                      {order.payOnSite ? (
                        <Chip size='small' variant='outlined' color='warning' label='Vor Ort zahlen' />
                      ) : (
                        <Chip size='small' variant='outlined' color='success' label='Online bezahlt' />
                      )}
                    </Stack>
                    <Typography variant='body2' sx={{ mt: 0.5 }}>
                      {(order.pizzaItems || []).map(i => `${i.count}× ${i.name}`).join(', ')}
                      {(order.otherItems || []).length > 0 && (
                        <Typography component='span' color='text.secondary'>
                          {' '}· dazu: {order.otherItems.map(i => `${i.count}× ${i.name}`).join(', ')}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, whiteSpace: 'nowrap' }}>
                    <Typography variant='caption' color='text.secondary' display='block'>
                      Bestellt um
                    </Typography>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {order.orderedAt ? dayjs(order.orderedAt).format('HH:mm:ss') : '—'}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        )}

        {canRead && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, sm: 2 },
              border: '1px solid',
              borderColor: 'divider',
              background:
                theme.palette.mode === 'light'
                  ? alpha(theme.palette.background.paper, 0.96)
                  : alpha(theme.palette.background.paper, 0.78),
            }}
          >
            {isLoading ? (
              <PizzaTimelineSkeleton />
            ) : visibleBookings.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant='h6'>Keine laufenden oder kommenden Pizza-Buchungen</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Erledigte Einträge verschwinden automatisch eine Stunde nach dem Abhaken.
                </Typography>
              </Box>
            ) : (
              <Stack spacing={{ xs: 2, sm: 3 }}>
                {visibleBookings.map((booking, idx) => (
                  <PizzaTimelineEntry
                    key={booking._id}
                    booking={booking}
                    isLast={idx === visibleBookings.length - 1}
                    rowLoading={!!rowLoading[booking._id]}
                    serviceLoading={!!serviceLoading[booking._id]}
                    canUpdateDelivery={canUpdateDelivery}
                    nowTs={nowTs}
                    onToggleDelivery={handleToggleDelivery}
                    onOpenDetails={handleOpenDetails}
                    onTableNumberChange={handleTableNumberChange}
                    onServiceTimeSave={handleServiceTimeSave}
                    onCommentSave={handleCommentSave}
                    onItemDeliveredChange={handleItemDeliveredChange}
                    onAllIncDeliveredChange={handleAllIncDeliveredChange}
                    onAllIncVarietyChange={handleAllIncVarietyChange}
                  />
                ))}
              </Stack>
            )}
          </Paper>
        )}
      </Stack>

      <BookingDetailsModal
        open={detailsModal.open}
        onClose={handleCloseDetails}
        bookingId={detailsModal.bookingId}
        borderColor={detailsModal.borderColor}
        onUpdate={() => load({ showLoading: false })}
      />

      <AddPizzaDialog
        open={addDialogOpen}
        options={pizzaOptions}
        loading={pizzaOptionsLoading}
        selectedBookingId={selectedAddBookingId}
        selectedUpgradeId={selectedAddUpgradeId}
        count={selectedAddCount}
        fullScreen={isMobile}
        onBookingChange={setSelectedAddBookingId}
        onUpgradeChange={setSelectedAddUpgradeId}
        onCountChange={setSelectedAddCount}
        onClose={handleCloseAddDialog}
        onSubmit={handleAddManualItem}
      />

      <Dialog
        open={!!pendingPresenceBooking}
        onClose={() => {
          if (!presenceLoading) setPendingPresenceBooking(null);
        }}
        maxWidth='xs'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Buchung noch nicht anwesend</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary'>
            Diese Buchung ist noch nicht als anwesend markiert. Soll die Gruppe jetzt als
            anwesend markiert und die Pizza direkt als ausgegeben abgehakt werden?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingPresenceBooking(null)} disabled={presenceLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmPresenceAndDelivery}
            variant='contained'
            disabled={presenceLoading}
            startIcon={presenceLoading ? <CircularProgress size={16} /> : null}
          >
            Als anwesend markieren
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PizzaView;
